import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay } from 'rxjs/operators';
import { ApiErrorFactory } from '../../core/api-errors';
import { ManageTemplatesRouteParams } from '../../core/routeparams';
import { Template } from '../../core/template';
import { CreateNewTemplateService } from '../create-new-template';
import { FormFields } from '../create-progress/form-fields';
import { ErrorDialog } from '../dialog';
import { ManageTemplatesService } from './manage-templates.service';

enum LIMITS {
  THREE_COLUMNS = 1740,
  TWO_COLUMNS = 1270,
  MAX_TEMPLATES = 12,
  MIL_MAX_TEMPLATES = 24,
  VWCT_MAX_TEMPLATES = 1000,
  MAX_TEMPLATES_2D9C = 60,
}

@Component({
  selector: 'app-manage-templates',
  templateUrl: './manage-templates.component.html',
  styleUrls: ['./manage-templates.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ManageTemplatesComponent implements OnInit {
  loading$: Observable<boolean>;
  templates$: Observable<Template[]>;
  templatesCreating$: Observable<Template[]>;
  templateCreatingIds$: Observable<string[]>;
  maxTemplates: number;
  backLink: string;
  partnerId: string;
  columns: number;
  dialogRef: MatDialogRef<ErrorDialog>;
  withinTemplateLimit$: Observable<boolean>;
  rowHeight: number;
  parentTemplateId: string;
  templatesAll$: Observable<Template[]>;
  distributedTemplates$: Observable<Template[]>;
  nonDistributedTemplates$: Observable<Template[]>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiErrorFactory: ApiErrorFactory,
    private dialog: MatDialog,
    private manageTemplatesService: ManageTemplatesService,
    private createNewTemplateService: CreateNewTemplateService,
    private snowplow: ProductAnalyticsService,
  ) {
    const routeParams = this.route.parent.snapshot.params as ManageTemplatesRouteParams;
    this.partnerId = routeParams.partnerId;
    this.templates$ = this.manageTemplatesService.templates$;
    this.templatesCreating$ = this.manageTemplatesService.templatesCreating$;
    this.templateCreatingIds$ = this.templatesCreating$.pipe(
      map((templates) => templates.map((template) => template.siteId)),
    );
    this.templatesAll$ = combineLatest([this.templates$, this.templatesCreating$]).pipe(
      // makes sure concat will not apply if templates1 is undefined
      filter(([templates1]) => !!templates1),
      // makes sure disabled buttons won't flash if it reached maxTemplates
      distinctUntilChanged(
        (oldTemplates, newTemplates) =>
          newTemplates[1].length <= oldTemplates[1].length && newTemplates[0].length === oldTemplates[0].length,
      ),
      map(([templates1, templates2]) => templates1.concat(templates2)),
      // Share subscription between derived observables to prevent redundant filtering
      shareReplay(1),
    );

    // Filter distributed templates (is_template_distributed = true)
    this.distributedTemplates$ = this.templatesAll$.pipe(
      map((templates) => templates.filter((template) => template.isTemplateDistributed)),
    );

    // Filter non-distributed templates (is_template_distributed = false)
    this.nonDistributedTemplates$ = this.templatesAll$.pipe(
      map((templates) => templates.filter((template) => !template.isTemplateDistributed)),
    );
    this.loading$ = this.manageTemplatesService.templateLoading$;
    this.setMaxTemplates();
    this.rowHeight = 484;

    this.withinTemplateLimit$ = this.templatesAll$.pipe(
      map((templates: Template[]) => !!templates && !(templates.length >= this.maxTemplates)),
    );
  }

  ngOnInit(): void {
    this.manageTemplatesService.loadTemplates(this.partnerId);
    this.addDuplicatedTemplates();
    this.calcColumns(window.innerWidth);
  }

  setMaxTemplates(): void {
    switch (this.partnerId) {
      case 'MIL':
        this.maxTemplates = LIMITS.MIL_MAX_TEMPLATES;
        break;
      case 'VWCT':
        this.maxTemplates = LIMITS.VWCT_MAX_TEMPLATES;
        break;
      case '2D9C':
        this.maxTemplates = LIMITS.MAX_TEMPLATES_2D9C;
        break;
      default:
        this.maxTemplates = LIMITS.MAX_TEMPLATES;
    }
  }

  addNew(): void {
    this.router.navigateByUrl(`${this.partnerId}/templates/manage/create`);
  }

  onResize(event: any): void {
    this.calcColumns(event.target.innerWidth);
  }

  calcColumns(width: number): void {
    if (width >= LIMITS.THREE_COLUMNS) {
      this.columns = 3;
    } else if (width >= LIMITS.TWO_COLUMNS) {
      this.columns = 2;
    } else {
      this.columns = 1;
    }
  }

  handleError(errorResponse: HttpErrorResponse): void {
    const parsedError = this.apiErrorFactory.parseErrorResponse(errorResponse);
    this.displayError(parsedError.getUserMessage());
  }

  deleteTemplateFromList(isDeleted: boolean, template: Template): void {
    this.manageTemplatesService.deleteTemplateFromList(template.siteId);
  }

  getParentTemplateID(templateId: string): void {
    this.parentTemplateId = templateId;
  }

  duplicateTemplate(duplicationForm: FormFields): void {
    this.createNewTemplateService.setFormFields(duplicationForm);
    this.createNewTemplateService.createTemplate(this.partnerId, this.parentTemplateId).subscribe(
      () => {
        this.addDuplicatedTemplates();
      },
      (err) => this.handleError(err),
    );
  }

  addDuplicatedTemplates(): void {
    if (!this.manageTemplatesService.isDuplicating) {
      this.manageTemplatesService.loadTemplates(this.partnerId, true);
    }
  }

  private displayError(error: string): void {
    this.dialogRef = this.dialog.open(ErrorDialog, {
      data: { errMsg: error },
    });
  }
}
